import * as React from "react";
import FileItem from "./FileItem";
import { useEffect, useMemo, useRef, useState } from "react";
import { FileData } from "@/utils/types";
import { useDispatch, useSelector } from "react-redux";
import {
  DocumentArrowUpIcon,
  DocumentPlusIcon,
  FolderPlusIcon,
  FolderArrowDownIcon,
} from "@heroicons/react/20/solid";
import { cn } from "@/lib/utils";
import axios from "axios";
import { toast } from "sonner";
import {
  getAxiosErorrMessage,
  logAxiosError,
  useCanvasId,
} from "@/utils/utils";
import { fetchFileTree } from "@/store/codebaseSlice";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { EditorContext, useCodebaseStore } from "@/store/zustand/codebaseStore";
import { useSession } from "@clerk/clerk-react";
import { getUnexpiredToken } from "@/utils/utils";
import useActivityStore from "@/store/zustand/activityStore";

interface FileContextMenuProps {
  onDelete: () => void;
  onOpen: () => void;
  children: React.ReactNode;
  file: FileData;
}

const FileContextMenu = React.memo(
  ({ onDelete, onOpen, children, file }: FileContextMenuProps) => {
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

    const DeleteConfirmation = React.useMemo(() => {
      const basename = file.fullPath.split("/").pop();
      const message =
        file.type === "folder" ? (
          <p>
            This action cannot be undone. This will permanently remove the{" "}
            <code className="font-bold text-xs">{basename}</code> folder and all
            of the files inside.
          </p>
        ) : (
          <p>
            This will permanently remove the{" "}
            <code className="font-bold text-xs">{basename}</code> file.
          </p>
        );
      return (
        <AlertDialog open={true} onOpenChange={setShowDeleteConfirmation}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
              <AlertDialogDescription>{message}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="pt-4">
              <AlertDialogCancel
                onClick={() => setShowDeleteConfirmation(false)}
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                variant="destructive"
                onClick={() => {
                  setShowDeleteConfirmation(false);
                  onDelete();
                }}
              >
                Continue
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      );
    }, [file]);

    if (showDeleteConfirmation) {
      return DeleteConfirmation;
    }

    return (
      <ContextMenu>
        <ContextMenuTrigger>{children}</ContextMenuTrigger>
        <ContextMenuContent className="w-64">
          <ContextMenuItem onClick={onOpen}>Open</ContextMenuItem>
          <ContextMenuItem onClick={() => setShowDeleteConfirmation(true)}>
            Delete
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>
    );
  },
);

interface Props {}

const FileSelector: React.FunctionComponent<Props> = React.memo((props) => {
  const canvasId = useCanvasId();
  const dispatch = useDispatch<any>();

  const { session } = useSession();

  const editorContext = useCodebaseStore((state) => state.editorContext);
  const hardOpenFile = useCodebaseStore((state) => state.hardOpenFile);
  const openFile = useCodebaseStore((state) => state.openFile);
  const updateLookupsBasedOnPartialResponse = useCodebaseStore(
    (state) => state.updateLookupsBasedOnPartialResponse,
  );

  const triggerRefetchActivityStream = useActivityStore(
    (state) => state.triggerRefetchActivityStream,
  );

  const fileTree = useSelector((state: any) => state.codebase.fileTree);
  const dir = fileTree?.[0]?.fullPath?.split("/").slice(0, -1).join("/") || "";

  const fileTreeDecorated: FileData = useMemo(() => {
    return {
      fullPath: dir,
      type: "folder",
      items: fileTree,
    };
  }, [dir, fileTree]);

  const [selectedFile, setSelectedFile] = useState<FileData | null>(
    fileTreeDecorated,
  );

  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(
    new Set([dir]), // Add the dir to the default set
  );

  const handleFileClick = (file: FileData) => {
    setSelectedFile(file);

    if (file.type == "folder") {
      const newExpandedFolders = new Set(expandedFolders);
      if (newExpandedFolders.has(file.fullPath)) {
        newExpandedFolders.delete(file.fullPath);
      } else {
        newExpandedFolders.add(file.fullPath);
      }
      setExpandedFolders(newExpandedFolders);
    } else {
      openFile({
        file: {
          filename: file.fullPath,
          context: EditorContext.Code,
        },
        jumpToTab: true,
      });
    }
  };

  const onFileDoubleClick = (file: FileData) => {
    if (file.type == "file") {
      hardOpenFile({
        filename: file.fullPath,
        context: editorContext,
      });
    }
  };

  const renameFile = (file: FileData, newPath: string) => {
    const body = {
      filename: file.fullPath,
      newPath: newPath,
    };

    if (!session) return;
    getUnexpiredToken(session).then((supabaseJWT) => {
      axios
        .post(
          `${
            import.meta.env.VITE_BACKEND_BASE_URL
          }/canvases/${canvasId}/renameFile`,
          body,
          {
            headers: {
              Authorization: `Bearer ${supabaseJWT}`,
            },
          },
        )
        .then((res: any) => {
          if (res?.data) {
            updateLookupsBasedOnPartialResponse(res.data);
            triggerRefetchActivityStream();
          }

          dispatch(
            fetchFileTree({
              session,
              canvasId,
            }),
          );

          const message = "File renamed";

          toast.success(message);
        })
        .catch((err: any) => {
          logAxiosError("Error renaming file", err);
          const message = "Error renaming file";

          toast.error(message + ": " + getAxiosErorrMessage(err));
        });
    });
  };

  const deleteFile = (file: FileData) => {
    const isDirectory = file.type === "folder";

    const body = {
      filename: file.fullPath,
      isDirectory,
    };

    if (!session) return;
    getUnexpiredToken(session).then((supabaseJWT) => {
      axios
        .post(
          `${
            import.meta.env.VITE_BACKEND_BASE_URL
          }/canvases/${canvasId}/removeFile`,
          body,
          {
            headers: {
              Authorization: `Bearer ${supabaseJWT}`,
            },
          },
        )
        .then((res: any) => {
          dispatch(
            fetchFileTree({
              session,
              canvasId,
            }),
          );

          let message = "File removed";

          if (isDirectory) {
            message = "Folder removed";
          }

          toast.success(message);
        })
        .catch((err: any) => {
          let message = "Error removing file";
          if (isDirectory) {
            message = "Error remove folder";
          }
          logAxiosError(message, err);

          toast.error(message + ": " + getAxiosErorrMessage(err));
        });
    });
  };

  const renderTree = (file: FileData, level: number): React.ReactNode => {
    const isFolder = selectedFile?.type === "folder";
    const currentDirectory: string | null = isFolder
      ? selectedFile.fullPath
      : dir;

    const isCurrentDirectory = file.fullPath === currentDirectory;
    const hasChildren = file.items && file.items.length > 0;
    const isExpanded = expandedFolders.has(file.fullPath); // Check if this specific node should be expanded

    // Separate folders and files (only if it's a folder)
    const folders =
      file.type === "folder" && hasChildren
        ? // @ts-expect-error TS18048
          file.items.filter((item) => item.type === "folder")
        : [];
    const files =
      file.type === "folder" && hasChildren
        ? // @ts-expect-error TS18048
          file.items.filter((item) => item.type === "file")
        : [];

    return (
      <React.Fragment key={file.fullPath}>
        <FileContextMenu
          onDelete={() => {
            deleteFile(file);
          }}
          onOpen={() => {
            hardOpenFile({ filename: file.fullPath, context: editorContext });
          }}
          file={file}
        >
          <FileItem
            key={file.fullPath}
            file={file}
            level={level}
            selected={selectedFile?.fullPath === file.fullPath}
            onClick={(e) => {
              e.stopPropagation();
              handleFileClick(file);
            }}
            onDoubleClick={() => onFileDoubleClick(file)}
            expanded={isExpanded}
            onEditSubmit={(oldPath, newPath) => {
              renameFile(file, newPath);
            }}
          />
        </FileContextMenu>

        {isExpanded && file.type === "folder" && (
          <React.Fragment key={file.fullPath + "-expanded"}>
            {folders.map((folder) => renderTree(folder, level + 1))}
            {/* Render input fields if this is the currently selected directory */}
            {selectedFile?.fullPath === file.fullPath && showNewFolderInput && (
              <FileInput
                placeholder="New folder"
                onExit={() => setShowNewFolderInput(false)}
                onSubmit={(name: string) => {
                  createFile(`${currentDirectory}/${name}`, true);
                  setShowNewFolderInput(false);
                }}
                level={level + 1}
              />
            )}
            {files.map((file) => renderTree(file, level + 1))}

            {isCurrentDirectory && showNewFileInput && (
              <FileInput
                onExit={() => setShowNewFileInput(false)}
                onSubmit={(name: string) => {
                  createFile(`${currentDirectory}/${name}`, false);
                  setShowNewFileInput(false);
                }}
                level={level + 1}
              />
            )}
          </React.Fragment>
        )}
      </React.Fragment>
    );
  };

  const [showNewFileInput, setShowNewFileInput] = useState(false);
  const [showNewFolderInput, setShowNewFolderInput] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const createFile = (file: File | string, isDirectory: boolean) => {
    // Check if a file is being uploaded
    const isUpload = file instanceof File;

    const formData = new FormData();
    formData.append("filename", isUpload ? file.name : file);
    formData.append("isDirectory", isDirectory.toString());
    if (isUpload) {
      formData.append("file", file as File);
    }

    if (selectedFile?.type === "folder") {
      formData.append("directoryToUploadTo", selectedFile.fullPath);
    }

    if (!session) return;
    getUnexpiredToken(session).then((supabaseJWT) => {
      axios
        .post(
          `${
            import.meta.env.VITE_BACKEND_BASE_URL
          }/canvases/${canvasId}/createFile`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${supabaseJWT}`,
              "Content-Type": "multipart/form-data",
            },
          },
        )
        .then((res: any) => {
          if (res?.data) {
            updateLookupsBasedOnPartialResponse(res.data);
            triggerRefetchActivityStream();
          }

          dispatch(
            fetchFileTree({
              session,
              canvasId,
            }),
          );

          let message = "File created";
          if (isDirectory) {
            message = "Folder created";
          } else if (isUpload) {
            message = "File uploaded";
          }

          const fileDataInstantUpdate: FileData = {
            fullPath: isUpload ? file.name : file,
            type: isDirectory ? "folder" : "file",
          };

          handleFileClick(fileDataInstantUpdate);

          toast.success(message);
        })
        .catch((err: any) => {
          let message = "Error creating file";
          if (isDirectory) {
            message = "Error creating folder";
          } else if (isUpload) {
            message = "Error uploading file";
          }
          logAxiosError(message, err);

          toast.error(message + ": " + getAxiosErorrMessage(err));
        });
    });
  };

  const downloadCanvas = async () => {
    const downloadUrl = `${
      import.meta.env.VITE_BACKEND_BASE_URL
    }/canvases/${canvasId}/download`;

    let url = "";

    if (!session) return;
    const supabaseJWT = await getUnexpiredToken(session);

    try {
      const response = await axios({
        url: downloadUrl,
        method: "GET",
        responseType: "blob",
        headers: {
          Authorization: `Bearer ${supabaseJWT}`,
        },
      });

      const disposition = response.headers["content-disposition"];
      const filenameMatch = disposition?.match(/filename="?([^"]+)"?/);
      const filename = filenameMatch?.[1] ?? `tempo-${canvasId}.zip`;

      url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      logAxiosError("Error downloading project", err);
      toast.error("Error downloading project: " + getAxiosErorrMessage(err));
    } finally {
      if (url) {
        window.URL.revokeObjectURL(url); // Ensure cleanup even if errors occur before link removal
      }
    }
  };

  const handleFileUploadClick = () => {
    // @ts-expect-error TS18047
    fileInputRef.current.click();
  };

  const handleFileChange = (event: { target: { files: FileList | null } }) => {
    const file = event.target.files?.[0];
    if (file) {
      createFile(file, false); // Assuming false means it's not a directory
    }
  };

  const FileActions = () => {
    return (
      <div className="w-full flex items-center justify-end border-b p-1">
        <div className="space-x-1 flex flex-row items-center justify-center">
          <div
            className={cn(
              "w-4 h-4 flex justify-center items-center",
              showNewFileInput
                ? "text-white"
                : "text-secondary-900 hover:text-white cursor-pointer",
            )}
            data-tooltip-id="tooltip"
            data-tooltip-place="bottom"
            data-tooltip-content="New file"
            onClick={() => {
              if (
                // @ts-expect-error TS18047
                selectedFile.type === "folder" &&
                // @ts-expect-error TS18047
                !expandedFolders.has(selectedFile.fullPath)
              ) {
                setExpandedFolders(
                  // @ts-expect-error TS18047
                  new Set(expandedFolders).add(selectedFile.fullPath),
                );
              }
              setShowNewFileInput(true);
            }}
          >
            <DocumentPlusIcon className="w-full stroke-1" />
          </div>
          <div
            className={cn(
              "w-4 h-4 flex justify-center items-center",
              showNewFolderInput
                ? "text-white"
                : "text-secondary-900 hover:text-white cursor-pointer",
            )}
            onClick={() => {
              if (
                // @ts-expect-error TS18047
                selectedFile.type === "folder" &&
                // @ts-expect-error TS18047
                !expandedFolders.has(selectedFile.fullPath)
              ) {
                setExpandedFolders(
                  // @ts-expect-error TS18047
                  new Set(expandedFolders).add(selectedFile.fullPath),
                );
              }
              setShowNewFolderInput(true);
            }}
            data-tooltip-id="tooltip"
            data-tooltip-place="bottom"
            data-tooltip-content="New folder"
          >
            <FolderPlusIcon className="w-full stroke-1" />
          </div>
          <div
            className="w-4 h-4 flex justify-center items-center text-secondary-900 hover:text-white cursor-pointer"
            data-tooltip-id="tooltip"
            data-tooltip-place="bottom"
            data-tooltip-content="Upload file"
            onClick={() => handleFileUploadClick()}
          >
            <DocumentArrowUpIcon className="w-full stroke-1" />
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
          </div>
          <div
            className="w-4 h-4 flex justify-center items-center text-secondary-900 hover:text-white cursor-pointer"
            data-tooltip-id="tooltip"
            data-tooltip-place="bottom"
            data-tooltip-content="Download codebase"
            onClick={downloadCanvas}
          >
            <FolderArrowDownIcon className="w-full stroke-1" />
          </div>
        </div>
      </div>
    );
  };

  interface FileInputProps {
    onSubmit: (name: string) => void;
    onExit: () => void;
    placeholder?: string;
    level: number;
  }

  const FileInput: React.FC<FileInputProps> = ({
    onSubmit,
    onExit,
    placeholder = "New file",
    level,
  }: FileInputProps) => {
    const [name, setName] = useState("");
    const inputRef = useRef<HTMLInputElement>(null);

    const handleSubmitAttempt = () => {
      if (name.length > 0) {
        onSubmit(name);
      } else {
        onExit();
      }
    };

    const handleKeyDown = (e: { key: string }) => {
      if (e.key === "Enter") {
        inputRef.current?.blur();
      } else if (e.key === "Escape") {
        onExit();
      }
    };

    /**
     * Effect to ensure on render the input is focused
     */
    useEffect(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, [inputRef.current]);

    return (
      <div
        className="flex flex-row"
        style={{
          paddingLeft: `${20 * level + 4}px`,
        }}
      >
        <img
          alt={placeholder}
          src={"/icons/file-extension-items/seti-ui/default.svg"}
          width="20"
          height="20"
          style={{
            boxSizing: "initial",
          }}
          className="max-w-none"
        />
        <input
          ref={inputRef}
          className="w-full file-item rounded-none focus:outline-hidden bg-background"
          placeholder={placeholder}
          value={name}
          onInput={(e) => setName(e.currentTarget.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => handleSubmitAttempt()}
        ></input>
      </div>
    );
  };

  const renderedTree = React.useMemo(() => {
    if (!fileTreeDecorated) {
      return null;
    }
    return fileTree.map((rootItem: FileData) => renderTree(rootItem, 0));
  }, [
    fileTree,
    expandedFolders,
    selectedFile?.fullPath,
    showNewFileInput,
    showNewFolderInput,
  ]);

  return (
    <div className="flex flex-col w-full h-full" data-testid="file-selector">
      <FileActions />
      <div
        className="file-selector overflow-y-scroll"
        onClick={() => setSelectedFile(fileTreeDecorated)}
      >
        {renderedTree}
      </div>
    </div>
  );
});

FileSelector.displayName = "FileSelector";

export default FileSelector;
